import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { DataKeys } from "../../../types";
import { useLang } from "../../Context";
import { getEffectiveLocationHash } from "../../navigation/navPathUtils";
import { HealthSection } from "../HealthSection";
import { HealthSectionHeader } from "../HealthSectionHeader";
import {
    getLocalized,
    HealthLang,
    LocalizedString,
} from "../healthTypes";
import {
    healthAimmaDoctorSectionContentOffset,
} from "../healthLayout";
import {
    healthClinicalAgentsActiveWrap,
    healthClinicalAgentsShell,
} from "../healthVisualStyles";
import { HealthClinicalAgentDetailPanel } from "./HealthClinicalAgentDetailPanel";
import { HealthClinicalAgentTabSelector } from "./HealthClinicalAgentTabSelector";
import { getAimmaDoctorAgentPreviewSrc } from "./healthAimmaDoctorAgentPreviewAssets";

interface HealthClinicalAgentsSectionProps {
    data: DataKeys;
    clinicalInputsLabel: string;
    outputsLabel: string;
    physicianReviewLabel: string;
    workflowBenefitLabel: string;
}

export function HealthClinicalAgentsSection({
    data,
    clinicalInputsLabel,
    outputsLabel,
    physicianReviewLabel,
    workflowBenefitLabel,
}: HealthClinicalAgentsSectionProps) {
    const { lang } = useLang();
    const location = useLocation();
    const hLang = lang as HealthLang;
    const blocks = (data.blocks as DataKeys[]) ?? [];
    const [activeIndex, setActiveIndex] = useState(0);
    const prevIndexRef = useRef(0);
    const panelIdPrefix = "agents";

    const selectAgent = (index: number) => {
        prevIndexRef.current = activeIndex;
        setActiveIndex(index);
    };

    useEffect(() => {
        const hash = getEffectiveLocationHash(location.hash);

        if (!hash || hash === "#agents") {
            setActiveIndex((current) => {
                if (current === 0) return current;
                prevIndexRef.current = current;
                return 0;
            });
            return;
        }

        if (!hash.startsWith("#agents-")) return;

        const productId = hash.replace("#agents-", "");
        const index = blocks.findIndex((block) => block.id === productId);
        if (index >= 0) {
            setActiveIndex((current) => {
                prevIndexRef.current = current;
                return index;
            });
        }
    }, [blocks, location.hash, lang]);

    const activeBlock = blocks[activeIndex];
    const activeProductId = activeBlock?.id as string | undefined;
    const transitionDirection =
        activeIndex >= prevIndexRef.current ? 1 : -1;
    const previewSrc = getAimmaDoctorAgentPreviewSrc(activeProductId);
    const previewAlt = activeBlock
        ? getLocalized(
              (activeBlock.visual as DataKeys)?.alt as LocalizedString,
              hLang
          )
        : "Clinical agent interface preview";
    const summaryLabel = getLocalized(
        data.summaryLabel as LocalizedString,
        hLang
    );
    const capabilitiesLabel = getLocalized(
        data.capabilitiesLabel as LocalizedString,
        hLang
    );
    const tabListAriaLabel = getLocalized(
        data.tabListAriaLabel as LocalizedString,
        hLang
    );
    const tabPreviousAriaLabel = getLocalized(
        data.tabPreviousAriaLabel as LocalizedString,
        hLang
    );
    const tabNextAriaLabel = getLocalized(
        data.tabNextAriaLabel as LocalizedString,
        hLang
    );
    return (
        <HealthSection
            id="agents"
            ariaLabelledBy="agents-title"
            compact
            className="scroll-mt-24"
        >
            <HealthSectionHeader
                eyebrow={getLocalized(data.eyebrow as LocalizedString, hLang)}
                title={getLocalized(data.title as LocalizedString, hLang)}
                titleId="agents-title"
                subtitle={getLocalized(data.intro as LocalizedString, hLang)}
                subtitleWide
            />

            <div
                className={`${healthAimmaDoctorSectionContentOffset} ${healthClinicalAgentsShell}`}
            >
                <HealthClinicalAgentTabSelector
                    blocks={blocks}
                    activeIndex={activeIndex}
                    onSelect={selectAgent}
                    lang={hLang}
                    panelIdPrefix={panelIdPrefix}
                    tabListAriaLabel={tabListAriaLabel}
                    tabPreviousAriaLabel={tabPreviousAriaLabel}
                    tabNextAriaLabel={tabNextAriaLabel}
                />

                <div className={healthClinicalAgentsActiveWrap}>
                    {activeBlock ? (
                        <HealthClinicalAgentDetailPanel
                            block={activeBlock}
                            lang={hLang}
                            transitionDirection={transitionDirection}
                            clinicalInputsLabel={clinicalInputsLabel}
                            outputsLabel={outputsLabel}
                            physicianReviewLabel={physicianReviewLabel}
                            workflowBenefitLabel={workflowBenefitLabel}
                            summaryLabel={summaryLabel}
                            capabilitiesLabel={capabilitiesLabel}
                            panelId={`${panelIdPrefix}-${activeProductId}`}
                            tabId={`${panelIdPrefix}-${activeProductId}-tab`}
                            previewSrc={previewSrc}
                            previewAlt={previewAlt}
                        />
                    ) : null}
                </div>
            </div>
        </HealthSection>
    );
}
